import { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../api/client';
import { Client } from '../types';

/**
 * Référentiel des clients (mandants et donneurs d'ordre) : hiérarchie
 * auto-référentielle (ex. Canton de Vaud › DGCS › DIRIS), coordonnées et
 * personne de contact. Ces fiches sont consommées par les modules Revenus
 * (mandant d'un flux) et Contrats (partie contractante).
 */
export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  // null = fermé ; { client: null } = création ; { client } = édition
  const [modal, setModal] = useState<{ client: Client | null } | null>(null);

  const charger = useCallback(() => {
    setErreur(null);
    api.get<Client[]>('/clients')
      .then(setClients)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Aplatit l'arbre : racines puis enfants, avec profondeur pour l'indentation
  const arbre = useMemo(() => {
    const parParent = new Map<number | null, Client[]>();
    for (const c of clients) {
      const p = c.parent_id ?? null;
      if (!parParent.has(p)) parParent.set(p, []);
      parParent.get(p)!.push(c);
    }
    const resultat: Array<{ client: Client; profondeur: number }> = [];
    const visiter = (parentId: number | null, profondeur: number) => {
      for (const c of parParent.get(parentId) ?? []) {
        resultat.push({ client: c, profondeur });
        visiter(c.id, profondeur + 1);
      }
    };
    visiter(null, 0);
    // Orphelins éventuels (parent inactif supprimé de la liste) — par sécurité
    for (const c of clients) {
      if (!resultat.some((r) => r.client.id === c.id)) resultat.push({ client: c, profondeur: 0 });
    }
    return resultat;
  }, [clients]);

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Clients</h1>
          <span className="stat-pill">{clients.length} fiches</span>
        </div>
        <button className="btn btn-accent" onClick={() => setModal({ client: null })}>Nouveau client</button>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <table className="table table-compact">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Contact</th>
            <th>Coordonnées</th>
            <th>Adresse</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          {arbre.map(({ client: c, profondeur }) => (
            <tr key={c.id} title={c.note ?? undefined} style={{ cursor: 'pointer' }} onClick={() => setModal({ client: c })}>
              <td>
                <span style={{ paddingLeft: profondeur * 18 }}>
                  {profondeur > 0 && <span className="text-ink-3">└ </span>}
                  <span className="font-semi">{c.nom}</span>
                  {c.acronyme && <span className="badge badge-sm" style={{ marginLeft: 8 }}>{c.acronyme}</span>}
                </span>
              </td>
              <td>{c.contact_nom ?? '—'}</td>
              <td className="text-sm">
                {[c.contact_email, c.contact_telephone].filter(Boolean).join(' · ') || '—'}
              </td>
              <td className="text-sm">{c.adresse ?? '—'}</td>
              <td>{c.actif
                ? <span className="badge badge-ok badge-sm">actif</span>
                : <span className="badge badge-sm">inactif</span>}</td>
            </tr>
          ))}
          {arbre.length === 0 && <tr><td colSpan={5} className="text-ink-3">Aucun client.</td></tr>}
        </tbody>
      </table>

      <p className="text-xs text-ink-3 mt-8">
        La hiérarchie représente les niveaux administratifs d'un même mandant (ex. Canton de Vaud › DGCS › DIRIS) :
        les contrats et les flux de revenus se rattachent au niveau le plus précis qui fait foi.
      </p>

      {modal && (
        <ClientModal
          client={modal.client}
          clients={clients}
          onFermer={() => setModal(null)}
          onModifie={() => { setModal(null); charger(); }}
        />
      )}
    </div>
  );
}

function ClientModal({ client, clients, onFermer, onModifie }: {
  client: Client | null; // null = création
  clients: Client[];
  onFermer: () => void;
  onModifie: () => void;
}) {
  const [nom, setNom] = useState(client?.nom ?? '');
  const [acronyme, setAcronyme] = useState(client?.acronyme ?? '');
  const [parentId, setParentId] = useState<number | null>(client?.parent_id ?? null);
  const [adresse, setAdresse] = useState(client?.adresse ?? '');
  const [contactNom, setContactNom] = useState(client?.contact_nom ?? '');
  const [contactEmail, setContactEmail] = useState(client?.contact_email ?? '');
  const [contactTelephone, setContactTelephone] = useState(client?.contact_telephone ?? '');
  const [note, setNote] = useState(client?.note ?? '');
  const [actif, setActif] = useState(client?.actif ?? true);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  // Parents possibles : tout client sauf soi-même et ses descendants
  const parentsPossibles = useMemo(() => {
    if (!client) return clients;
    const descendants = new Set<number>([client.id]);
    let taille = 0;
    while (descendants.size !== taille) {
      taille = descendants.size;
      for (const c of clients) {
        if (c.parent_id != null && descendants.has(c.parent_id)) descendants.add(c.id);
      }
    }
    return clients.filter((c) => !descendants.has(c.id));
  }, [client, clients]);

  const valide = nom.trim().length > 0 && commentaire.trim().length > 0;

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    const corps = {
      nom: nom.trim(),
      acronyme: acronyme.trim() || null,
      parentId,
      adresse: adresse.trim() || null,
      contactNom: contactNom.trim() || null,
      contactEmail: contactEmail.trim() || null,
      contactTelephone: contactTelephone.trim() || null,
      note: note.trim() || null,
      commentaire: commentaire.trim(),
    };
    try {
      if (client) {
        await api.put(`/clients/${client.id}`, { ...corps, actif });
      } else {
        await api.post('/clients', corps);
      }
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{client ? client.nom : 'Nouveau client'}</div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          <div className="flex flex-col gap-12">
            <div className="flex gap-12 flex-wrap">
              <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
                <label className="form-label">Nom</label>
                <input className="form-input w-full" value={nom} onChange={(e) => setNom(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Acronyme</label>
                <input className="form-input" style={{ width: 110 }} value={acronyme} onChange={(e) => setAcronyme(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Rattaché à <span className="text-ink-3">(niveau parent — vide = racine)</span></label>
              <select className="form-input w-full" value={parentId ?? ''} onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">— aucun (racine) —</option>
                {parentsPossibles.map((c) => (
                  <option key={c.id} value={c.id}>{c.acronyme ? `${c.nom} (${c.acronyme})` : c.nom}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Adresse</label>
              <input className="form-input w-full" value={adresse} onChange={(e) => setAdresse(e.target.value)} />
            </div>
            <div className="flex gap-12 flex-wrap">
              <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                <label className="form-label">Personne de contact</label>
                <input className="form-input w-full" value={contactNom} onChange={(e) => setContactNom(e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                <label className="form-label">Email</label>
                <input type="email" className="form-input w-full" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Téléphone</label>
                <input className="form-input" style={{ width: 140 }} value={contactTelephone} onChange={(e) => setContactTelephone(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Note</label>
              <input className="form-input w-full" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            {client && (
              <label className="toggle-label">
                <span className="toggle">
                  <input type="checkbox" className="toggle-input" checked={actif} onChange={(e) => setActif(e.target.checked)} />
                  <span className="toggle-slider" />
                </span>
                <span className="text-sm">Client actif</span>
              </label>
            )}
            <div className="form-group">
              <label className="form-label">Commentaire de modification (obligatoire)</label>
              <input className="form-input w-full" placeholder="Motif de la création ou de la modification…"
                value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            {erreur && <div className="text-err text-sm">{erreur}</div>}
            <div className="flex justify-end gap-8">
              <button className="btn" onClick={onFermer}>Annuler</button>
              <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                {enCours ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
